import React, { useState } from 'react';
import styled from 'styled-components';
import dayjs, { type Dayjs } from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import { createCampaign } from '../api/campaings';
import { NOTIFICATION_TYPE_ENCUESTA } from '../constants/globals';
import { formatLessThan10 } from '../utils/formatLessThan10';

const TITLE_MAX = 100;
const BODY_MAX = 160;

export default function EncuestasNotificaciones(): JSX.Element {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedTime, setSelectedTime] = useState<Dayjs>(dayjs());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const confirmDate = (): void => {
    setShowDatePicker(false);
    const date = new Date(
      selectedDate.year(),
      selectedDate.month(),
      selectedDate.date(),
      selectedTime.hour(),
      selectedTime.minute(),
    );
    setScheduledAt(date);
  };

  const getScheduledLabel = (): string => {
    if (!scheduledAt) return 'Selecciona fecha y hora';
    return `${scheduledAt.getDate()}/${formatLessThan10(scheduledAt.getMonth() + 1)}/${scheduledAt.getFullYear()} ${formatLessThan10(scheduledAt.getHours())}:${formatLessThan10(scheduledAt.getMinutes())}`;
  };

  const handleSubmit = (): void => {
    setError('');
    setSuccess(false);
    if (!title.trim()) { setError('El título es obligatorio.'); return; }
    if (!body.trim()) { setError('El cuerpo es obligatorio.'); return; }
    if (!scheduledAt) { setError('Selecciona la fecha y hora de envío.'); return; }
    if (scheduledAt <= new Date()) { setError('La fecha debe ser en el futuro.'); return; }
    setConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    try {
      await createCampaign(
        title,
        body,
        scheduledAt,
        undefined,
        '',
        undefined,
        undefined,
        NOTIFICATION_TYPE_ENCUESTA,
      );
      setSuccess(true);
      setTitle('');
      setBody('');
      setScheduledAt(null);
    } catch {
      setError('Ocurrió un error al enviar la notificación. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Container>
      <TitleContainer>
        <Title>Encuestas</Title>
      </TitleContainer>

      <FormCard>
        <SectionTitle>Nueva notificación de encuesta</SectionTitle>
        <Description>
          Esta notificación llegará a todos los usuarios y al tocarla serán dirigidos directamente a la sección de Encuestas.
        </Description>

        <FieldLabel>Título <Required>*</Required> <Limit>({title.length}/{TITLE_MAX})</Limit></FieldLabel>
        <StyledInput
          placeholder='Título de la notificación'
          value={title}
          maxLength={TITLE_MAX}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTitle(e.target.value); }}
          disabled={sending}
        />

        <FieldLabel>Cuerpo <Required>*</Required> <Limit>({body.length}/{BODY_MAX})</Limit></FieldLabel>
        <StyledTextArea
          placeholder='Descripción de la notificación'
          value={body}
          maxLength={BODY_MAX}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setBody(e.target.value); }}
          disabled={sending}
          rows={3}
        />

        <FieldLabel>Fecha y hora de envío <Required>*</Required></FieldLabel>
        <div
          onClick={() => { if (!sending) setShowDatePicker(!showDatePicker); }}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', height: 40, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 12px', marginBottom: 4 }}
        >
          <p style={{ fontSize: 14, color: '#374151', margin: 0 }}>{getScheduledLabel()}</p>
        </div>

        {showDatePicker && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginTop: 8 }}>
              <DateCalendar
                minDate={dayjs()}
                value={selectedDate}
                onChange={(v) => { if (v) setSelectedDate(v); }}
              />
              <MultiSectionDigitalClock
                ampm={false}
                value={selectedTime}
                onChange={(v) => { if (v) setSelectedTime(v); }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button onClick={confirmDate}><ButtonText>OK</ButtonText></Button>
            </div>
          </LocalizationProvider>
        )}

        {error !== '' && <ErrorText>{error}</ErrorText>}
        {success && <SuccessText>¡Notificación programada exitosamente!</SuccessText>}

        <SendButton onClick={handleSubmit} disabled={sending}>
          {sending ? 'Enviando...' : 'Enviar notificación (a todos)'}
        </SendButton>
      </FormCard>

      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); }}>
        <DialogTitle>¿Confirmar envío?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta notificación se enviará a <strong>todos los usuarios</strong> el <strong>{getScheduledLabel()}</strong>. ¿Estás seguro?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); }}>Cancelar</Button>
          <Button onClick={() => { void handleConfirm(); }} variant='contained' color='primary'>
            Sí, enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

const Container = styled.section`
  padding-left: 24px;
  padding-right: 24px;
  margin-left: 192px;
  margin-top: 72px;
  width: calc(100vw - 192px);

  @media screen and (max-width: 860px) {
    margin-left: 0px;
    width: 100vw;
    margin-top: 70px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 42px;
  max-width: 560px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.p`
  font-size: 28px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  color: ${(props) => props.theme.colors.primary};
  flex: 1;
`;

const FormCard = styled.div`
  margin-top: 24px;
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 560px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  margin-left: auto;
  margin-right: auto;
`;

const SectionTitle = styled.p`
  font-size: 18px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const FieldLabel = styled.p`
  font-size: 13px;
  font-family: ${(props) => props.theme.fonts.medium};
  color: #374151;
  margin-bottom: 6px;
  margin-top: 16px;
`;

const Limit = styled.span`
  color: #9ca3af;
  font-weight: normal;
`;

const Required = styled.span`
  color: #ef4444;
`;


const ButtonText = styled.p`
  text-transform: none;
  font-family: ${(props) => props.theme.fonts.medium};
  color: ${(props) => props.theme.colors.primary};
  font-size: 13px;
  padding: 0 15px;
`;

const StyledInputBase = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  &:focus { border-color: ${(props) => props.theme.colors.primary}; }
`;
const StyledInput = StyledInputBase as unknown as React.FC<React.InputHTMLAttributes<HTMLInputElement>>;

const StyledTextAreaBase = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  resize: vertical;
  &:focus { border-color: ${(props) => props.theme.colors.primary}; }
`;
const StyledTextArea = StyledTextAreaBase as unknown as React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>>;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 13px;
  margin-top: 12px;
`;

const SuccessText = styled.p`
  color: #16a34a;
  font-size: 13px;
  margin-top: 12px;
`;

const SendButtonBase = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 12px;
  background-color: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.medium};
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.9; }
`;
const SendButton = SendButtonBase as unknown as React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>>;
